import { useSuspenseQuery } from "@tanstack/react-query";
import {
  apiFetchEasySharedPages,
  apiFetchSharedPage,
  apiFetchSharedPages,
  apiSaveSharedPage,
} from "modules/Drupal/Drupal";
import { TRoute, useRouteStates } from "./RouteModel";
import { useLeftNavItems } from "globalStates/leftNavState";
import { TRouteCardLayouts, useCardLayouts } from "./CardLayoutModel";
import { TCard, useCards } from "./CardModel";
import { useSearchValuesState2 } from "globalStates/cardSearchValuesState";
import { useCallback } from "react";
import { useUser } from "./UserModel";

export type TSharedPage = {
  id?: string;
  status: boolean;
  title: string;
  createUserId: string;
  routeId: string;
  baseRoute: TRoute;
  contents: {
    cards: TCard[];
    layouts: TRouteCardLayouts["layouts"];
  };
  shitenCD?: any;
};

export const useEasySharedPages = (uid: string, routeId: string) => {
  const { data } = useSuspenseQuery({
    queryKey: ["useFetchEasySharedPages"],
    queryFn: () => apiFetchEasySharedPages(uid, routeId),
  });
  return { data };
};

export const useSharedPages = () => {
  const { currentRoute, routeIds, setRoute, setRouteIds } = useRouteStates();
  const { allItems, resetItemIds } = useLeftNavItems();
  const { addCard, deleteRouteCards, getAllCards } = useCards();
  const { setSearchValues } = useSearchValuesState2();
  const { getAllCardLayouts, addCardLayout2 } = useCardLayouts();
  const { patchUser } = useUser();

  const { data: sharedPages, refetch } = useSuspenseQuery({
    queryKey: ["useFetchSharedPages"],
    queryFn: apiFetchSharedPages,
  });

  const sharedRouteIds = sharedPages.map((p) => p.routeId);

  const isSharedPageBaseRoute = (route: TRoute) => route && sharedRouteIds.includes(route.id);

  const isDisabledSharedPageBaseRoute = (route: TRoute) => {
    const sp = sharedPages.find((p) => p.routeId === route.id);
    return sp?.status === false;
  };

  const isSharedPageUseRoute = (route: TRoute) => route && route.type === "shared_page";

  const save = async (mode: "post" | "patch", data: TSharedPage) => {
    return await apiSaveSharedPage(mode, data);
  };

  const loadAndSaveSharedPage = useCallback(
    async (mode: "new" | "reload", sharedPageId: TSharedPage["id"]) => {
      const sharedPage = await apiFetchSharedPage(sharedPageId);
      if (!sharedPage || !sharedPage.baseRoute || !sharedPage.contents) return;
      const newRoute = mode === "new" ? { ...sharedPage.baseRoute } : { ...currentRoute };

      if (mode === "new") {
        newRoute.id = window.crypto.randomUUID();
        newRoute.url = "/" + newRoute.id;
        newRoute.type = "shared_page";
        newRoute.sharedPageId = sharedPageId;
        newRoute.title = sharedPage.title;
        delete newRoute.depth;
        delete newRoute.parentId;
        const newRouteIds = [...routeIds];
        newRouteIds.push(newRoute.id);
        setRoute(newRoute);
        setRouteIds(newRouteIds);
        await resetItemIds();
      } else if (mode === "reload") {
        newRoute.type = "shared_page";
        newRoute.sharedPageId = sharedPageId;
        newRoute.title = sharedPage.title;
        await deleteRouteCards(currentRoute);
        setRoute(newRoute);
        await resetItemIds();
      }

      const layouts = JSON.parse(JSON.stringify(sharedPage.contents.layouts)) as TRouteCardLayouts;
      layouts.route_id = newRoute.id;
      for (const card of sharedPage.contents.cards) {
        const addedCard = await addCard(card, newRoute.id);
        await setSearchValues(addedCard, addedCard.search_values);
        Object.keys(layouts.layouts).forEach((key) => {
          layouts.layouts[key].forEach((layout, index) => {
            if (layout.i === card.uuid) layouts.layouts[key][index]["i"] = addedCard.uuid;
          });
        });
      }
      await addCardLayout2(layouts);

      const newRoutes =
        mode === "new" ? [...allItems, newRoute] : allItems.map((i) => (i && i.id === newRoute.id ? newRoute : i));
      const newCards = [...(await getAllCards())];
      const newLayouts = [...(await getAllCardLayouts())];
      await patchUser({ routes: newRoutes, cards: newCards, layouts: newLayouts });

      return newRoute;
    },
    [
      allItems,
      getAllCards,
      getAllCardLayouts,
      patchUser,
      routeIds,
      setRoute,
      setRouteIds,
      resetItemIds,
      currentRoute,
      deleteRouteCards,
      addCardLayout2,
      addCard,
      setSearchValues,
    ],
  );

  return {
    sharedPages,
    refetch,
    save,
    sharedRouteIds,
    isSharedPageBaseRoute,
    isDisabledSharedPageBaseRoute,
    isSharedPageUseRoute,
    loadAndSaveSharedPage,
  };
};
